/**
 * AdminDashboard Page
 *
 * Main admin dashboard providing platform overview, metrics, and recent activity.
 * Displays key statistics, system alerts, and actionable insights.
 *
 * Epic: MVP 1.21 - Comprehensive Dashboard & Account Management
 * Story: SCRUM-148 - Admin Dashboard (21 SP)
 * Updated: MVP 1.21.2 - Dashboard Design System (SCRUM-459)
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { formatCentsToDollars } from '../../utils/formHelpers';
import {
  Users as _Users,
  UserCheck,
  TrendingUp as _TrendingUp,
  ShoppingBag,
  DollarSign,
  Star as _Star,
  Calendar,
  Activity as ActivityIcon
} from 'lucide-react';
import { StatsCard } from '../../components/admin/dashboard/StatsCard';
import { AlertsWidget } from '../../components/admin/dashboard/AlertsWidget';
import { RecentActivityWidget } from '../../components/admin/dashboard/RecentActivityWidget';
import { useAdminDashboard } from '../../hooks/admin/useAdminDashboard';
import DashboardLayout from '../../components/layout/DashboardLayout';

export function AdminDashboard() {
  const { data, loading, error, refresh } = useAdminDashboard();
  const { user: authUser } = useAuth();
  const navigate = useNavigate();

  const handleNavigate = (id, path) => {
    if (path) {
      navigate(path);
    }
  };

  const overview = data?.overview || {};
  const activity = data?.activity || {};
  const alerts = data?.alerts || {};
  const quickStats = data?.quick_stats || {};

  return (
    <DashboardLayout
      userRole="admin"
      activeNavId="dashboard"
      onNavigate={handleNavigate}
      user={{
        name: authUser?.fullName || authUser?.firstName || 'Admin',
        email: authUser?.email || '',
        avatar: authUser?.profilePicture || null
      }}
    >
      <div style={{ marginBottom: 'var(--space-6)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--space-4)' }}>
          <h1 style={{ fontSize: 'var(--font-size-2xl)', fontWeight: 'var(--font-weight-bold)', color: 'var(--color-text-primary)', margin: 0 }}>
            Admin Dashboard
          </h1>
          <button
            onClick={refresh}
            disabled={loading}
            style={{
              padding: 'var(--space-2) var(--space-4)',
              backgroundColor: loading ? 'var(--color-gray-300)' : 'var(--color-primary)',
              color: 'var(--color-white)',
              border: 'none',
              borderRadius: 'var(--radius-md)',
              fontSize: 'var(--font-size-sm)',
              fontWeight: 'var(--font-weight-medium)',
              cursor: loading ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--space-2)',
              transition: 'all var(--transition-fast)',
            }}
          >
            <ActivityIcon size={16} />
            Refresh
          </button>
        </div>
      </div>
      {error ? (
        <div style={{
          backgroundColor: 'var(--color-error-light)',
          border: '1px solid var(--color-error)',
          borderRadius: 'var(--radius-lg)',
          padding: 'var(--space-4)'
        }}>
          <h3 style={{
            color: 'var(--color-error)',
            fontWeight: 'var(--font-weight-semibold)',
            marginBottom: 'var(--space-2)'
          }}>
            Error Loading Dashboard
          </h3>
          <p style={{
            color: 'var(--color-error)',
            fontSize: 'var(--font-size-sm)',
            marginBottom: 'var(--space-3)'
          }}>
            {error}
          </p>
          <button
            onClick={refresh}
            style={{
              padding: 'var(--space-2) var(--space-4)',
              backgroundColor: 'var(--color-error)',
              color: 'var(--color-white)',
              border: 'none',
              borderRadius: 'var(--radius-md)',
              fontSize: 'var(--font-size-sm)',
              fontWeight: 'var(--font-weight-medium)',
              cursor: 'pointer',
              transition: 'all var(--transition-fast)',
            }}
          >
            Retry
          </button>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-6)' }}>
        {/* Quick Stats Row */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
          <StatsCard
            title="Bookings Today"
            value={quickStats.bookings_today || 0}
            icon={ShoppingBag}
            color="green"
            loading={loading}
          />
          <StatsCard
            title="Signups Today"
            value={quickStats.signups_today || 0}
            icon={_Users}
            color="blue"
            loading={loading}
          />
          <StatsCard
            title="Revenue Today"
            value={formatCentsToDollars(quickStats.revenue_today)}
            icon={DollarSign}
            color="purple"
            loading={loading}
          />
          <StatsCard
            title="Reviews Today"
            value={quickStats.reviews_today || 0}
            icon={_Star}
            color="orange"
            loading={loading}
          />
        </div>

        {/* Overview Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
          <StatsCard
            title="Total Customers"
            value={overview.users?.total_customers || 0}
            icon={_Users}
            trend={overview.users?.new_users_7d ? Math.round((overview.users.new_users_7d / overview.users.total_customers) * 100) : 0}
            trendLabel="last 7 days"
            color="blue"
            loading={loading}
          />
          <StatsCard
            title="Active Guides"
            value={overview.guides?.approved || 0}
            icon={UserCheck}
            trend={overview.guides?.pending ? -Math.round((overview.guides.pending / overview.guides.approved) * 100) : 0}
            trendLabel={`${overview.guides?.pending || 0} pending`}
            color="green"
            loading={loading}
          />
          <StatsCard
            title="Total Bookings (7d)"
            value={overview.bookings?.bookings_7d || 0}
            icon={ShoppingBag}
            trend={overview.bookings?.confirmed ? Math.round((overview.bookings.bookings_7d / overview.bookings.confirmed) * 100) : 0}
            trendLabel="vs confirmed"
            color="purple"
            loading={loading}
          />
          <StatsCard
            title="Revenue (30d)"
            value={formatCentsToDollars(overview.revenue?.revenue_30d)}
            icon={_TrendingUp}
            color="orange"
            loading={loading}
          />
        </div>

        {/* Platform Stats Row */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
          <StatsCard
            title="Active Experiences"
            value={overview.experiences?.active || 0}
            icon={Calendar}
            trendLabel={`${overview.experiences?.total || 0} total`}
            color="blue"
            loading={loading}
          />
          <StatsCard
            title="Avg Rating"
            value={overview.reviews?.avg_rating ? parseFloat(overview.reviews.avg_rating).toFixed(1) : '0.0'}
            icon={_Star}
            trendLabel={`${overview.reviews?.reviews_7d || 0} reviews (7d)`}
            color="yellow"
            loading={loading}
          />
          <StatsCard
            title="Pending Bookings"
            value={overview.bookings?.pending || 0}
            icon={ShoppingBag}
            color="orange"
            loading={loading}
          />
          <StatsCard
            title="Active Users (7d)"
            value={quickStats.active_users_7d || 0}
            icon={ActivityIcon}
            color="purple"
            loading={loading}
          />
        </div>

        {/* SCRUM-2176: link to System Health dashboard (MVP 1.29.5-05) */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
          <button
            type="button"
            onClick={() => navigate('/admin/system-health')}
            style={{
              padding: 'var(--space-4)',
              backgroundColor: 'var(--color-white)',
              border: '1px solid var(--color-border, #e5e7eb)',
              borderLeft: '4px solid #0d9488',
              borderRadius: 'var(--radius-lg)',
              cursor: 'pointer',
              textAlign: 'left',
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--space-3)',
            }}
          >
            <ActivityIcon size={28} color="#0d9488" />
            <div>
              <div style={{ fontSize: 'var(--font-size-base)', fontWeight: 600 }}>System Health</div>
              <div style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-secondary, #6b7280)' }}>
                Real-time module status &amp; last-24h numbers
              </div>
            </div>
          </button>
        </div>

        {/* Alerts and Activity Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2">
            <RecentActivityWidget activity={activity} loading={loading} />
          </div>
          <div>
            <AlertsWidget alerts={alerts} loading={loading} />
          </div>
        </div>
        </div>
      )}
    </DashboardLayout>
  );
}

export default AdminDashboard;
